import { computed, onMounted, onUnmounted } from "vue";
import { useHost } from "vue";
import { useSharedState } from "composables/useSharedState";
import { createEventDispatcher } from "@/runtime/shared/events";
import { getNestedInputs, setupAutofocus } from "components/Input/useInput";
import { isValidInputKey } from "composables/useKeyboardValidation";
import type { InputOtpProps } from "./inputOtp.types";
import { inputOtpClasses } from "./inputOtp.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";

export interface UseInputOtpOptions {
  props: Readonly<InputOtpProps>;
}

/** Shared context passed to module-level event handlers. */
interface OtpContext {
  inputElements: HTMLInputElement[];
  fieldValues: () => string[];
  currentLength: () => number;
  currentType: () => string;
  updateModelValue: (values: string[]) => void;
  /** Set by mousedown on host or focusout host-check. Consumed by handleFocus. */
  movingWithinGroup: boolean;
}

/**
 * Focus and select the input at the given index.
 */
const selectInputAtIndex = (ctx: OtpContext, index: number): void => {
  const input = ctx.inputElements[index];
  if (input) {
    input.focus();
    input.select();
  }
};

/**
 * Handle input event on a specific field.
 */
const handleInput = (ctx: OtpContext, e: Event, index: number): void => {
  const input = e.target as HTMLInputElement;
  const char = input.value.slice(0, 1);

  const newFieldValues = [...ctx.fieldValues()];
  newFieldValues[index] = char;
  ctx.updateModelValue(newFieldValues);

  if (char && index < ctx.currentLength() - 1) {
    setTimeout(() => selectInputAtIndex(ctx, index + 1), 0);
  }
};

/**
 * Handle keydown event for navigation and validation.
 */
const handleKeydown = (ctx: OtpContext, e: KeyboardEvent, index: number): void => {
  const input = e.target as HTMLInputElement;

  if (e.key === "Tab") return;

  if (!isValidInputKey(e, ctx.currentType() as "number" | "text")) {
    e.preventDefault();
    return;
  }

  if (e.key === "Backspace" && !input.value && index > 0) {
    e.preventDefault();
    const newFieldValues = [...ctx.fieldValues()];
    newFieldValues[index - 1] = "";
    ctx.updateModelValue(newFieldValues);
    selectInputAtIndex(ctx, index - 1);
    return;
  }

  if (e.key === "ArrowLeft" && index > 0) {
    e.preventDefault();
    selectInputAtIndex(ctx, index - 1);
    return;
  }

  const maxIndex = getTargetInputIndex(ctx.fieldValues());
  if (e.key === "ArrowRight" && index < maxIndex) {
    e.preventDefault();
    selectInputAtIndex(ctx, index + 1);
  }
};

/**
 * Handle paste event to distribute characters across fields.
 */
const handlePaste = (ctx: OtpContext, e: ClipboardEvent, index: number): void => {
  e.preventDefault();

  const pastedText = e.clipboardData?.getData("text") || "";
  if (!pastedText) return;

  const pattern = ctx.currentType() === "number" ? /\d/g : /[a-z\d]/gi;
  const validChars = pastedText.match(pattern) || [];

  const maxChars = ctx.currentLength() - index;
  const charsToUse = validChars.slice(0, maxChars);

  const newFieldValues = [...ctx.fieldValues()];
  for (const [i, char] of charsToUse.entries()) {
    newFieldValues[index + i] = char;
  }
  ctx.updateModelValue(newFieldValues);

  const lastFilledIndex = index + charsToUse.length - 1;
  if (lastFilledIndex < ctx.currentLength()) {
    setTimeout(() => selectInputAtIndex(ctx, lastFilledIndex), 0);
  }
};

/**
 * Find the target input index for initial focus.
 * Returns the first empty input, or the last input if all are filled.
 */
const getTargetInputIndex = (values: string[]): number => {
  for (const [i, value] of values.entries()) {
    if (!value) return i;
  }
  return values.length - 1;
};

/**
 * Update tabindex to control tab navigation.
 * Only one input should be tabbable at a time.
 */
const updateTabIndex = (inputs: HTMLInputElement[], focusedIndex: number): void => {
  for (const [index, input] of inputs.entries()) {
    input.tabIndex = index === focusedIndex ? 0 : -1;
  }
};

/**
 * Traverse shadow roots to find the deepest active element.
 */
const getDeepActiveElement = (): Element | null => {
  let el: Element | null = document.activeElement;
  while (el?.shadowRoot?.activeElement) {
    el = el.shadowRoot.activeElement;
  }
  return el;
};

/**
 * Check whether an element belongs to the OTP input group.
 * Handles shadow DOM retargeting: when focus moves between inputs in different
 * shadow roots, `relatedTarget` is retargeted to the `<e-input>` host element
 * rather than the inner `<input>`. This checks both the inputs directly and
 * their shadow root hosts.
 */
const isElementInGroup = (el: Element | null, ctx: OtpContext): boolean => {
  if (!el) return false;
  if (ctx.inputElements.includes(el as HTMLInputElement)) return true;
  return ctx.inputElements.some((input) => {
    const root = input.getRootNode();
    return root instanceof ShadowRoot && root.host === el;
  });
};

/**
 * Handle mousedown on an `<e-input>` host element.
 * Fires BEFORE blur/focusout/focus — at this point `document.activeElement`
 * still points to the previously focused element, so we can reliably detect
 * whether focus is already inside the OTP group.
 */
const handleHostMouseDown = (ctx: OtpContext): void => {
  const active = getDeepActiveElement();
  if (active && ctx.inputElements.includes(active as HTMLInputElement)) {
    ctx.movingWithinGroup = true;
  }
};

/**
 * Handle focus event to select input content and manage tab order.
 * Uses `relatedTarget` with shadow-DOM-aware group check to distinguish
 * external focus (redirect to first empty) from internal navigation (allow).
 */
const handleFocus = (ctx: OtpContext, e: FocusEvent, index: number): void => {
  const input = e.target as HTMLInputElement;
  const fromInside = ctx.movingWithinGroup || isElementInGroup(e.relatedTarget as Element | null, ctx);
  ctx.movingWithinGroup = false;
  const maxIndex = getTargetInputIndex(ctx.fieldValues());

  if (!fromInside) {
    if (maxIndex !== index) {
      selectInputAtIndex(ctx, maxIndex);
      return;
    }
  } else if (index > maxIndex) {
    selectInputAtIndex(ctx, maxIndex);
    return;
  }

  input.select();
  updateTabIndex(ctx.inputElements, index);
};

/**
 * Handle focusout to reset tabIndex when focus leaves the group entirely,
 * so the correct input is tabbable for keyboard re-entry.
 */
const handleFocusOut = (ctx: OtpContext, e: FocusEvent): void => {
  const staysInGroup = ctx.movingWithinGroup || isElementInGroup(e.relatedTarget as Element | null, ctx);
  if (staysInGroup) {
    ctx.movingWithinGroup = true;
  } else {
    const targetIndex = getTargetInputIndex(ctx.fieldValues());
    updateTabIndex(ctx.inputElements, targetIndex);
  }
};

/**
 * Attaches all OTP event listeners to the input elements.
 * Returns a cleanup function.
 */
const attachListeners = (ctx: OtpContext): (() => void) => {
  const listeners: Array<{ element: Element; event: string; handler: EventListener }> = [];

  for (const [index, input] of ctx.inputElements.entries()) {
    const entries: Array<[string, EventListener]> = [
      ["input", (e: Event) => handleInput(ctx, e, index)],
      ["keydown", (e: Event) => handleKeydown(ctx, e as KeyboardEvent, index)],
      ["paste", (e: Event) => handlePaste(ctx, e as ClipboardEvent, index)],
      ["focus", (e: Event) => handleFocus(ctx, e as FocusEvent, index)],
      ["focusout", (e: Event) => handleFocusOut(ctx, e as FocusEvent)],
    ];

    input.tabIndex = index === 0 ? 0 : -1;

    for (const [event, handler] of entries) {
      input.addEventListener(event, handler);
      listeners.push({ element: input, event, handler });
    }

    // mousedown on the <e-input> host covers clicks on padding/border
    // that don't hit the native <input> directly.
    const root = input.getRootNode();
    if (root instanceof ShadowRoot) {
      const handler: EventListener = () => handleHostMouseDown(ctx);
      root.host.addEventListener("mousedown", handler);
      listeners.push({ element: root.host, event: "mousedown", handler });
    }
  }

  return () => {
    for (const { element, event, handler } of listeners) {
      element.removeEventListener(event, handler);
    }
  };
};

const buildOtpContext = (
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
  fieldValues: () => string[],
  currentLength: () => number,
  currentType: () => string,
): OtpContext => ({
  inputElements: [],
  movingWithinGroup: false,
  fieldValues,
  currentLength,
  currentType,
  updateModelValue(newFieldValues: string[]) {
    const concatenated = newFieldValues.join("");
    dispatchEvent("update:modelValue", concatenated);
    if (concatenated.length === currentLength()) {
      dispatchEvent("complete", concatenated);
    }
  },
});

interface OtpLifecycleArgs {
  ctx: ReturnType<typeof buildOtpContext>;
  host: ReturnType<typeof useHost>;
  state: ReturnType<typeof useSharedState<InputOtpProps>>;
}

/** Wire mount/unmount: gather nested inputs, attach listeners, install host.focus, autofocus first slot. */
const buildOtpLifecycle = ({ ctx, host, state }: OtpLifecycleArgs) => {
  let cleanup: (() => void) | null = null;
  const mount = () => {
    ctx.inputElements = getNestedInputs(host);
    cleanup = attachListeners(ctx);
    if (host) {
      (host as HTMLElement).focus = () => {
        const target = ctx.inputElements[getTargetInputIndex(ctx.fieldValues())] ?? ctx.inputElements[0];
        target?.focus();
      };
    }
    if (state.autofocus?.value && ctx.inputElements.length > 0) {
      setupAutofocus(ctx.inputElements[0], state.autofocusDelay?.value || 0);
      selectInputAtIndex(ctx, 0);
    }
  };
  const unmount = () => cleanup?.();
  return { mount, unmount };
};

export const useInputOtp = (options: UseInputOtpOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);

  const fieldValues = computed(() => {
    const value = state.modelValue?.value || "";
    const length = state.length?.value || 6;
    const chars = [...value].slice(0, length);
    while (chars.length < length) chars.push("");
    return chars;
  });

  const currentLength = computed(() => state.length?.value || 6);
  const currentType = computed(() => state.type?.value || "number");

  const ctx = buildOtpContext(
    dispatchEvent,
    () => fieldValues.value,
    () => currentLength.value,
    () => currentType.value,
  );

  const lifecycle = buildOtpLifecycle({ ctx, host, state });

  onMounted(lifecycle.mount);
  onUnmounted(lifecycle.unmount);

  const focusTargetInput = () => {
    if (ctx.inputElements.length === 0) return;
    selectInputAtIndex(ctx, getTargetInputIndex(fieldValues.value));
  };

  const fieldIndexes = computed(() => Array.from({ length: props.length || 6 }, (_, i) => i));
  const inputType = computed(() => (props.mask ? "password" : "text"));
  const inputmode = computed(() => (props.type === "number" ? "numeric" : "text"));
  const currentValue = computed(() => fieldValues.value.join(""));
  const wrapperClasses = mergeClassProp(`e-input-otp ${inputOtpClasses().base}`, props.class);

  return {
    fieldValues,
    focusTargetInput,
    fieldIndexes,
    inputType,
    inputmode,
    currentValue,
    wrapperClasses,
  };
};
